import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconStars from '@zvuk-b2b/react-uikit/ui/icons/Stars';

import { CommonModalProps } from 'application/providers/ModalProvider/modalsProps';
import { ModalTypes } from 'application/providers/ModalProvider/consts';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { useAppDispatch } from 'application/providers/StoreProvider';
import { useAccountCompanyId } from 'application/hooks';
import { withProviders } from 'application/helpers';
import PlayerProvider from 'application/providers/PlayerProvider';
import { setPromoStatuses, setStep } from 'application/pages/AdsPage/slices/createAdCampaign/slice';
import { CREATE_AD_CAMPAIGN_STEPS } from 'application/pages/AdsPage/slices/createAdCampaign/consts';
import { FetchPolicyKeys } from 'domain/api/graphql/types';
import {
  usePromoRollSynthCompanySettingsQuery,
  usePromoRollSynthVoicesQuery,
  usePromoRollSynthBackgroundsQuery,
  usePromoRollSynthJobsQuery,
  usePromoRollSynthCreateJobMutation,
  usePromoRollSynthDeleteJobMutation,
  PromoRollSynthJobsDocument,
} from 'domain/api/graphql/generated';

import { PromoRollSynthJobFieldsFragment } from '@entities/promo-roll-synth-job/model';
import { convertJobsToPromoStatuses } from '../../model/convertJobToPromoStatus';
import { usePromoRollSynthFallback } from '../../model/usePromoRollSynthFallback';
import { getDuplicateJobKind } from '../../model/getDuplicateJobKind';
import { isDailyLimitError } from '../../model/isDailyLimitError';
import { isDuplicateTitleError } from '../../model/isDuplicateTitleError';

import PromoRollSynthForm, { PromoRollSynthFormValues } from '../PromoRollSynthForm/PromoRollSynthForm';
import PromoRollSynthJobList from '../PromoRollSynthJobList/PromoRollSynthJobList';
import LimitInfo from '../LimitInfo/LimitInfo';
import { TEXTS } from './texts';
import { DATA_TEST_IDS, HIGHLIGHT_ANIMATION_DURATION_MS, JOBS_PAGE_SIZE } from './consts';
import styles from './PromoRollSynthModal.module.scss';

const PromoRollSynthModal: FC<CommonModalProps> = ({ close, replaceModal }) => {
  const companyId = useAccountCompanyId();
  const dispatch = useAppDispatch();
  const notification = useNotificationContext();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [highlightedJobId, setHighlightedJobId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<PromoRollSynthFormValues>({
    mode: 'onChange',
    defaultValues: { title: '', text: '', voiceId: '', backgroundId: '' },
  });

  const { data: settingsData, refetch: refetchSettings } = usePromoRollSynthCompanySettingsQuery({
    variables: { companyId },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    skip: !companyId,
  });

  const { data: voicesData } = usePromoRollSynthVoicesQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const { data: backgroundsData } = usePromoRollSynthBackgroundsQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const { data: jobsData, fetchMore } = usePromoRollSynthJobsQuery({
    variables: {
      filters: { companyId },
      pagination: { page: 0, pageSize: JOBS_PAGE_SIZE },
    },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    skip: !companyId,
  });

  const [createJob, { loading: creating }] = usePromoRollSynthCreateJobMutation();
  const [deleteJob] = usePromoRollSynthDeleteJobMutation();

  usePromoRollSynthFallback(companyId);

  const limits = settingsData?.promoRollSynthCompanySettings;
  const voices = voicesData?.promoRollSynthVoices ?? [];
  const backgrounds = backgroundsData?.promoRollSynthBackgrounds ?? [];
  const jobs = jobsData?.promoRollSynthJobs?.result ?? [];
  const paginationInfo = jobsData?.promoRollSynthJobs?.paginationInfo;
  const hasNextPage = paginationInfo?.hasNextPage ?? false;
  const remainingToday = limits?.remainingToday ?? 0;

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
    },
    []
  );

  const highlightJob = (jobId: string) => {
    if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);

    setHighlightedJobId(jobId);
    highlightTimeoutRef.current = setTimeout(() => setHighlightedJobId(null), HIGHLIGHT_ANIMATION_DURATION_MS);
  };

  const handleGenerate = handleSubmit(async (values) => {
    const existingJobIds = new Set(jobs.map((job) => job.id));

    try {
      const { data } = await createJob({
        variables: {
          input: {
            companyId,
            title: values.title,
            text: values.text,
            voiceId: values.voiceId,
            backgroundId: values.backgroundId,
          },
        },
        refetchQueries: [PromoRollSynthJobsDocument],
      });

      if (!data?.promoRollSynthCreate?.ok) {
        notification.showErrorNotification({ children: TEXTS.NOTIFICATION_CREATE_ERROR });
        return;
      }

      const createdJob = data.promoRollSynthCreate.job;
      const duplicateKind = getDuplicateJobKind(existingJobIds, createdJob);

      if (duplicateKind) {
        highlightJob(createdJob!.id);
      }

      if (duplicateKind === 'completed') {
        notification.showInfoNotification({ children: TEXTS.NOTIFICATION_CREATE_DUPLICATE_COMPLETED });
      } else if (duplicateKind === 'active') {
        notification.showInfoNotification({ children: TEXTS.NOTIFICATION_CREATE_DUPLICATE_ACTIVE });
      } else {
        notification.showInfoNotification({ children: TEXTS.NOTIFICATION_CREATE_SUCCESS });
      }
    } catch (error) {
      if (isDuplicateTitleError(error)) {
        notification.showErrorNotification({ children: TEXTS.NOTIFICATION_DUPLICATE_TITLE_ERROR });
      } else if (isDailyLimitError(error)) {
        notification.showErrorNotification({ children: TEXTS.NOTIFICATION_LIMIT_EXCEEDED });
      } else {
        notification.showErrorNotification({ children: TEXTS.NOTIFICATION_CREATE_ERROR });
      }
    } finally {
      refetchSettings();
    }
  });

  const handleDelete = async (id: string) => {
    try {
      const { data } = await deleteJob({
        variables: { input: { companyId, jobId: id } },
        refetchQueries: [PromoRollSynthJobsDocument],
      });

      if (!data?.promoRollSynthDelete?.ok) {
        notification.showErrorNotification({ children: TEXTS.NOTIFICATION_DELETE_ERROR });
        return;
      }

      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      notification.showInfoNotification({ children: TEXTS.NOTIFICATION_DELETE_SUCCESS });
    } catch {
      notification.showErrorNotification({ children: TEXTS.NOTIFICATION_DELETE_ERROR });
    }
  };

  const handleRetry = async (job: PromoRollSynthJobFieldsFragment) => {
    try {
      const { data } = await createJob({
        variables: {
          input: {
            companyId,
            title: job.title,
            text: '',
            voiceId: '',
            backgroundId: '',
          },
        },
        refetchQueries: [PromoRollSynthJobsDocument],
      });

      if (!data?.promoRollSynthCreate?.ok) {
        notification.showErrorNotification({ children: TEXTS.NOTIFICATION_CREATE_ERROR });
        return;
      }

      notification.showInfoNotification({ children: TEXTS.NOTIFICATION_CREATE_SUCCESS });
    } catch (error) {
      notification.showErrorNotification({
        children: isDuplicateTitleError(error)
          ? TEXTS.NOTIFICATION_DUPLICATE_TITLE_ERROR
          : TEXTS.NOTIFICATION_CREATE_ERROR,
      });
    }
  };

  const handleSelect = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleCreateCampaign = () => {
    const selectedJobs = jobs.filter((job) => selectedIds.has(job.id));
    dispatch(setPromoStatuses(convertJobsToPromoStatuses(selectedJobs)));
    dispatch(setStep(CREATE_AD_CAMPAIGN_STEPS.SETTINGS));
    replaceModal(ModalTypes.AD_CAMPAIGN_STEPPER_CREATE, { canBackToPromoRollSynth: true });
  };

  const handleEndReached = () => {
    if (!hasNextPage || !paginationInfo) {
      return;
    }
    fetchMore({
      variables: {
        filters: { companyId },
        pagination: { page: paginationInfo.page + 1, pageSize: JOBS_PAGE_SIZE },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          promoRollSynthJobs: {
            ...fetchMoreResult.promoRollSynthJobs,
            result: [...(prev.promoRollSynthJobs.result ?? []), ...(fetchMoreResult.promoRollSynthJobs.result ?? [])],
          },
        };
      },
    });
  };

  return (
    <ModalScreen
      elementId={DATA_TEST_IDS.modal}
      title={TEXTS.MODAL_TITLE}
      onClose={close}
      actionButtons={[
        <div
          key="generate-area"
          className={styles.generateArea}>
          <Button
            elementId={DATA_TEST_IDS.buttonGenerate}
            type={ButtonType.PLAIN}
            className={styles.generateButton}
            isDisabled={!isValid || creating || (limits ? remainingToday === 0 : false)}
            onClick={handleGenerate}>
            <IconStars />
            {TEXTS.BUTTON_GENERATE}
          </Button>
          {limits && (
            <LimitInfo
              remainingToday={remainingToday}
              dailyLimit={limits.dailyLimit}
            />
          )}
        </div>,
        <Button
          key="create-campaign"
          elementId={DATA_TEST_IDS.buttonCreateCampaign}
          type={selectedIds.size > 0 ? ButtonType.PRIMARY : ButtonType.SECONDARY}
          isDisabled={selectedIds.size === 0}
          onClick={handleCreateCampaign}>
          {TEXTS.BUTTON_CREATE_CAMPAIGN}
        </Button>,
        <Button
          key="close"
          elementId="promo_roll_synth_close"
          type={ButtonType.SECONDARY}
          onClick={() => close()}>
          {TEXTS.BUTTON_CLOSE}
        </Button>,
      ]}>
      <div className={styles.content}>
        <PromoRollSynthForm
          control={control}
          voices={voices}
          backgrounds={backgrounds}
          maxTitleLength={100}
          maxTextLength={limits?.maxTextLength ?? 300}
        />
        <PromoRollSynthJobList
          jobs={jobs}
          selectedIds={selectedIds}
          highlightedJobId={highlightedJobId}
          hasNextPage={hasNextPage}
          onEndReached={handleEndReached}
          onSelect={handleSelect}
          onDelete={handleDelete}
          onRetry={handleRetry}
        />
      </div>
    </ModalScreen>
  );
};

export default withProviders(PromoRollSynthModal, [PlayerProvider]);
